import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SectionHeader } from "@/components/SectionHeader";

const workItems = [
  {
    industry: "Real Estate",
    name: "Solo Realtor, Ontario",
    story:
      "New agent moved from a personal Gmail and a template site to a full brand system, professional website, Google Business Profile, business cards, and listing templates.",
    tags: ["Foundation → Presence", "Branding", "Website", "Listing Templates"],
  },
  {
    industry: "Mortgage",
    name: "Independent Mortgage Broker",
    story:
      "Rebuilt brand and website, added lead capture, review automation, and referral landing page. Referral partners now have something to send.",
    tags: ["Presence", "Website", "CRM", "Referral Page"],
  },
  {
    industry: "Insurance",
    name: "Independent Insurance Agency",
    story:
      "Agency-owned website, branded email for staff, Google Business Profile, and AI reception to catch missed calls during appointments.",
    tags: ["Agency Alignment", "AI Reception", "Google Profile"],
  },
  {
    industry: "Recruiting",
    name: "Boutique Recruiting Firm",
    story:
      "Turned a LinkedIn-only presence into a full firm brand: website, candidate landing pages, content system, and CRM.",
    tags: ["Agency Growth", "Website", "Content", "CRM"],
  },
  {
    industry: "Real Estate Team",
    name: "Growing Team, Toronto",
    story:
      "Team page structure, per-agent profile system, recruiting funnel, and unified listing template system across the roster.",
    tags: ["Agency Growth", "Team System", "Recruiting Funnel"],
  },
  {
    industry: "Financial Services",
    name: "Financial Advisor",
    story:
      "Refined brand, professional website, business email, and content system to support referrals from CPAs and lawyers.",
    tags: ["Presence", "Content", "Referral Support"],
  },
];

export default function Work() {
  return (
    <div>
      <section className="pt-16 md:pt-24 pb-16 md:pb-20 border-b border-[#27272a] relative overflow-hidden">
        <div className="absolute inset-0 radial-fade" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="eyebrow mb-6">
              <span className="gold-line" />
              Work
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05]">
              A look at what we <span className="text-[#C8A24B]">build.</span>
            </h1>
            <p className="mt-8 text-lg text-[#a1a1aa] leading-relaxed max-w-2xl">
              Every engagement is different. Below are anonymized examples that
              show how Agent Marketing helps professionals move from a
              patchwork presence to a system that supports the business.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-6 lg:gap-8">
          {workItems.map((w, i) => (
            <div
              key={i}
              data-testid={`work-card-${i}`}
              className="border border-[#27272a] hover:border-[#C8A24B]/50 bg-[#0e0e0e] rounded-sm transition-all p-8 md:p-10 relative overflow-hidden group"
            >
              <div className="absolute top-0 left-0 right-0 h-[1px] bg-[#C8A24B] opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="flex items-center justify-between mb-6">
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#C8A24B]">
                  {w.industry}
                </div>
                <ArrowUpRight size={16} className="text-[#52525b] group-hover:text-[#C8A24B]" />
              </div>
              <h3 className="font-heading text-2xl text-white mb-4">
                {w.name}
              </h3>
              <p className="text-[#d4d4d8] leading-relaxed mb-6">{w.story}</p>
              <div className="flex flex-wrap gap-2">
                {w.tags.map((t) => (
                  <span
                    key={t}
                    className="px-3 py-1.5 text-xs border border-[#27272a] text-[#a1a1aa] rounded-sm"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 md:py-28 border-t border-[#27272a] bg-[#0d0d0d]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <SectionHeader
            eyebrow="Your Turn"
            title="Bring your business into focus."
            subtitle="Every engagement starts with a short fit conversation. No pressure, no boilerplate pitch, just a look at what would move the business first."
            center
          />
          <Link to="/contact" className="btn-gold mt-10 inline-flex">
            Build My Growth Plan <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
